import { FieldErrors, useFormContext } from 'react-hook-form'
import { useCallback, useMemo } from 'react'
import { AuftragOption, AuftragOptionen, BefehlFormField } from '../Generic'
import { useTranslation } from 'react-i18next'
import { BetriebsstelleField, BetriebsstellenartField, Input } from '../../form'

export interface Auftrag95_50ZeileFormProps {
  auftragKennung: string
  fieldPrefix: string
  readOnly: boolean
  testId: string
  onRemove?: () => void
  disabled?: boolean
  activateEmptyFieldWarnings: boolean
  errors?: FieldErrors
}

function Auftrag95_50ZeileForm({
  fieldPrefix,
  readOnly,
  testId,
  disabled = false,
  activateEmptyFieldWarnings,
  errors
}: Readonly<Auftrag95_50ZeileFormProps>) {
  const { t } = useTranslation('ui-befehle')

  const formContext = useFormContext()
  const { control, setValue, formState } = formContext

  const fieldErrors = useMemo(() => {
    return errors ?? formState.errors
  }, [errors, formState.errors])

  const fieldPaths = useMemo(
    () => ({
      typePath: `${fieldPrefix}.type`,
      kmPath: `${fieldPrefix}.km`,
      signalPath: `${fieldPrefix}.signal`,
      betriebsstellenartPath: `${fieldPrefix}.zugmeldestelle.art`,
      zugmeldestellePath: `${fieldPrefix}.zugmeldestelle.name`
    }),
    [fieldPrefix]
  )

  const [watchedType, watchedBetriebsstellenart] = formContext.watch([
    fieldPaths.typePath,
    fieldPaths.betriebsstellenartPath
  ])

  const translationKeys = useMemo(
    () => ({
      km: `auftraege.95_51`,
      signal: `auftraege.95_52`,
      kmInputLabel: 'auftraege.1_10.zeile.km_input_label',
      signalInputLabel: 'auftraege.1_10.zeile.signal_input_label'
    }),
    []
  )

  const clearConflictingFields = useCallback(
    (newValue: string) => {
      if (newValue === 'km') {
        setValue(fieldPaths.signalPath, '', {
          shouldValidate: false,
          shouldDirty: false
        })
        setValue(fieldPaths.betriebsstellenartPath, '', {
          shouldValidate: false,
          shouldDirty: false
        })
        setValue(fieldPaths.zugmeldestellePath, '', {
          shouldValidate: false,
          shouldDirty: false
        })
      } else if (newValue === 'signal_zugmeldestelle') {
        setValue(fieldPaths.kmPath, '', {
          shouldValidate: false,
          shouldDirty: false
        })
      }
    },
    [setValue, fieldPaths]
  )

  const onOptionChange = useCallback(
    (newValue: string, fieldOnChange: (value: string) => void) => {
      fieldOnChange(newValue)
      clearConflictingFields(newValue)
    },
    [clearConflictingFields]
  )

  return (
    <div data-testid={`${testId}-container`}>
      <div className="flex items-center justify-end mb-0.5"></div>
      <BefehlFormField
        name={fieldPaths.typePath}
        control={control}
        label="Auftrag Option"
        readOnly={false}
        disabled={disabled}
        activateEmptyWarning={activateEmptyFieldWarnings}
        data-testid={`${testId}-feld-95-51-52-selector`}
        render={({ field }) => (
          <AuftragOptionen
            name={fieldPaths.typePath}
            value={field.value}
            disabled={disabled}
            onChange={(newValue) => onOptionChange(newValue, field.onChange)}
            readOnly={readOnly}
            errors={fieldErrors}
            data-testid={`${testId}-feld-95-51-52-selector-options`}
          >
            <AuftragOption
              value="km"
              label={t(translationKeys.km)}
              data-testid={`${testId}-feld-95-51-52-selector-options-km`}
            >
              <div className="flex w-full">
                <div className="w-full">
                  <BefehlFormField
                    name={fieldPaths.kmPath}
                    control={control}
                    label={t(translationKeys.kmInputLabel)}
                    readOnly={readOnly}
                    disabled={disabled || watchedType !== 'km'}
                    activateEmptyWarning={activateEmptyFieldWarnings}
                    data-testid={`${testId}-feld-95-51`}
                    variant="solid"
                    render={({ field: kmField, isDisabled: isKmDisabled }) => (
                      <Input
                        {...kmField}
                        label={t(translationKeys.kmInputLabel)}
                        errors={fieldErrors}
                        className="w-full"
                        data-testid={`${testId}-feld-95-51-inner`}
                        disabled={isKmDisabled}
                      />
                    )}
                  />
                </div>
              </div>
            </AuftragOption>
            <AuftragOption
              value="signal_zugmeldestelle"
              label={t(translationKeys.signal)}
              data-testid={`${testId}-feld-95-51-52-selector-options-km-signal`}
            >
              <div className="@container">
                <div className="flex flex-col @[400px]:flex-row w-full gap-0.5">
                  <div className="flex-1">
                    <BefehlFormField
                      name={fieldPaths.signalPath}
                      control={control}
                      label={t(translationKeys.signalInputLabel)}
                      readOnly={readOnly}
                      disabled={
                        disabled || watchedType !== 'signal_zugmeldestelle'
                      }
                      activateEmptyWarning={activateEmptyFieldWarnings}
                      data-testid={`${testId}-feld-95-52-signal`}
                      variant="solid"
                      render={({
                        field: signalField,
                        isDisabled: isSignalDisabled
                      }) => (
                        <Input
                          {...signalField}
                          label={t(translationKeys.signalInputLabel)}
                          errors={fieldErrors}
                          className="w-full"
                          data-testid={`${testId}-feld-95-52-signal-inner`}
                          disabled={isSignalDisabled}
                        />
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <BefehlFormField
                      name={fieldPaths.betriebsstellenartPath}
                      control={control}
                      label={t('auftraege.1_10.zeile.betriebsstellenart_label')}
                      readOnly={readOnly}
                      disabled={
                        disabled || watchedType !== 'signal_zugmeldestelle'
                      }
                      activateEmptyWarning={activateEmptyFieldWarnings}
                      data-testid={`${testId}-feld-95-52-art`}
                      variant="solid"
                      render={({
                        field: bstTypField,
                        isDisabled: isBstTypDisabled
                      }) => (
                        <BetriebsstellenartField
                          {...bstTypField}
                          label={t(
                            'auftraege.1_10.zeile.betriebsstellenart_label'
                          )}
                          variant="solid"
                          errors={fieldErrors}
                          className="w-full"
                          data-testid={`${testId}-feld-95-52-art-inner`}
                          disabled={isBstTypDisabled}
                          selectClassName="w-full"
                        />
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <BefehlFormField
                      name={fieldPaths.zugmeldestellePath}
                      control={control}
                      label={t('auftraege.1_10.zeile.zugmeldestelle_label')}
                      readOnly={readOnly}
                      disabled={
                        disabled || watchedType !== 'signal_zugmeldestelle'
                      }
                      activateEmptyWarning={activateEmptyFieldWarnings}
                      data-testid={`${testId}-feld-95-52-name`}
                      variant="solid"
                      render={({
                        field: zmField,
                        isDisabled: isZmDisabled
                      }) => (
                        <BetriebsstelleField
                          {...zmField}
                          label={t('auftraege.1_10.zeile.zugmeldestelle_label')}
                          errors={fieldErrors}
                          className="w-full"
                          data-testid={`${testId}-feld-95-52-name-inner`}
                          disabled={isZmDisabled}
                          typ={watchedBetriebsstellenart}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </AuftragOption>
          </AuftragOptionen>
        )}
      />
    </div>
  )
}

export default Auftrag95_50ZeileForm
